// src/pages/admin/JobLoader.jsx
// Page for loading job postings from URLs - standardized inline styles
// Added: Google Analytics tracking

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackPageView, trackJobCreated } from '../../utils/analytics';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://api.invenire.app';

export default function JobLoader() {
  const navigate = useNavigate();
  
  // Form state
  const [inputMode, setInputMode] = useState('url'); // 'url' or 'paste'
  const [url, setUrl] = useState('');
  const [pastedText, setPastedText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [parsedJob, setParsedJob] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [status, setStatus] = useState('');
  
  // Tenant state
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [loadingTenants, setLoadingTenants] = useState(true);
  
  // Jobs list
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);

  // Track page view on mount
  useEffect(() => {
    document.title = 'Invenire - Job Loader';
    trackPageView('/admin/jobs', 'Job Loader');
  }, []);

  // Load tenants on mount
  useEffect(() => {
    async function loadTenants() {
      try {
        const response = await fetch(`${API_BASE}/tenants`);
        if (response.ok) {
          const data = await response.json();
          const tenantList = Array.isArray(data) ? data : (data.tenants || []);
          setTenants(tenantList);
          if (tenantList.length > 0) {
            setSelectedTenantId(tenantList[0].id);
          }
        }
      } catch (err) {
        console.error('Failed to load tenants:', err);
        setError(`Failed to load clients: ${err.message}`);
      } finally {
        setLoadingTenants(false);
      }
    }
    loadTenants();
  }, []);

  // Load jobs when tenant changes
  const loadJobs = useCallback(async () => {
    if (!selectedTenantId) return;
    
    setLoadingJobs(true);
    try {
      const response = await fetch(`${API_BASE}/jobs/tenant/${selectedTenantId}`);
      if (response.ok) {
        const data = await response.json();
        setJobs(data.jobs || []);
      }
    } catch (err) {
      console.error('Failed to load jobs:', err);
    } finally {
      setLoadingJobs(false);
    }
  }, [selectedTenantId]);

  useEffect(() => {
    if (selectedTenantId) {
      loadJobs();
    }
  }, [selectedTenantId, loadJobs]);

  const handleParseJob = async (e) => {
    e.preventDefault();
    
    if (inputMode === 'url' && !url.trim()) {
      setError('Please enter a job URL');
      return;
    }
    
    if (inputMode === 'paste' && !pastedText.trim()) {
      setError('Please paste the job description');
      return;
    }
    
    if (!selectedTenantId) {
      setError('Please select a client');
      return;
    }
    
    setLoading(true);
    setError(null);
    setStatus(inputMode === 'url' ? 'Fetching job posting...' : 'Parsing job description...');
    setParsedJob(null);
    setJobId(null);
    
    try {
      const endpoint = inputMode === 'url' ? '/jobs/parse' : '/jobs/parse-text';
      const body = inputMode === 'url'
        ? { url: url.trim(), tenant_id: selectedTenantId }
        : { text: pastedText.trim(), tenant_id: selectedTenantId };
      
      const response = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      
      const data = await response.json();
      
      if (data.success) {
        setParsedJob(data.job);
        setJobId(data.job_id);
        setUrl('');
        setPastedText('');
        setStatus('Job imported successfully!');
        
        // Track job creation
        trackJobCreated(data.job_id, selectedTenantId, inputMode);
        
        loadJobs();
      } else {
        setError(data.error || 'Failed to parse job');
        setStatus('');
      }
    } catch (err) {
      console.error('Error parsing job:', err);
      setError(`Error: ${err.message}`);
      setStatus('');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteJob = async (jobIdToDelete) => {
    if (!window.confirm('Are you sure you want to delete this job?')) {
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE}/jobs/${jobIdToDelete}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        loadJobs();
      }
    } catch (err) {
      console.error('Failed to delete job:', err);
    }
  };

  const selectedTenant = tenants.find(t => t.id === selectedTenantId);

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.logoContainer}>
          <img 
            src="/invenire-logo-transparent.png" 
            alt="Invenire" 
            style={styles.logoImage}
          />
          <span style={styles.subtitle}>Job Loader</span>
        </div>
        <button
          style={styles.backButton}
          type="button"
          onClick={() => navigate("/admin")}
        >
          ← Admin Home
        </button>
      </div>

      <div style={styles.content}>
        {/* Hero */}
        <div style={styles.hero}>
          <h1 style={styles.heroTitle}>Job Loader</h1>
          <p style={styles.heroText}>
            Import job postings from URLs or paste the job description directly. 
            Invenire will automatically extract the title, requirements, responsibilities, and more.
          </p>
        </div>

        <div style={styles.grid}>
          {/* Left: Import Form */}
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>Import Job</h2>
            
            {/* Mode Toggle */}
            <div style={styles.modeToggle}>
              <button
                type="button"
                onClick={() => setInputMode('url')}
                style={inputMode === 'url' ? styles.modeButtonActive : styles.modeButton}
              >
                From URL
              </button>
              <button
                type="button"
                onClick={() => setInputMode('paste')}
                style={inputMode === 'paste' ? styles.modeButtonActive : styles.modeButton}
              >
                Paste Text
              </button>
            </div>
            
            <form onSubmit={handleParseJob}>
              {/* Client selector */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Client</label>
                <div style={{ marginTop: 4 }}>
                  {loadingTenants ? (
                    <span style={styles.loadingText}>Loading clients…</span>
                  ) : tenants.length === 0 ? (
                    <span style={styles.errorText}>No clients found. Add a tenant first.</span>
                  ) : (
                    <select
                      value={selectedTenantId}
                      onChange={(e) => setSelectedTenantId(e.target.value)}
                      disabled={loading}
                      style={styles.select}
                    >
                      <option value="">-- Select Client --</option>
                      {tenants.map((tenant) => (
                        <option key={tenant.id} value={tenant.id}>
                          {tenant.name || tenant.key}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>

              {/* URL input */}
              {inputMode === 'url' && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Job Posting URL</label>
                  <input
                    type="url"
                    placeholder="https://careers.company.com/job/123"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    disabled={loading}
                    style={styles.input}
                  />
                  <p style={styles.hint}>
                    Note: LinkedIn, Workday, and some sites may not work. Use "Paste Text" instead.
                  </p>
                </div>
              )}
              
              {/* Paste text input */}
              {inputMode === 'paste' && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Job Description</label>
                  <textarea
                    placeholder="Paste the full job description here..."
                    value={pastedText}
                    onChange={(e) => setPastedText(e.target.value)}
                    disabled={loading}
                    rows={8}
                    style={styles.textarea}
                  />
                  <p style={styles.hint}>
                    Copy the job title, description, requirements, etc. from the job posting page.
                  </p>
                </div>
              )}

              <button 
                type="submit" 
                style={styles.submitButton}
                disabled={loading || !selectedTenantId || (inputMode === 'url' ? !url.trim() : !pastedText.trim())}
              >
                {loading ? 'Importing...' : 'Import Job'}
              </button>

              {loading && (
                <div style={styles.statusText}>
                  {status || 'Processing...'}
                </div>
              )}
            </form>

            {error && (
              <pre style={styles.errorBox}>{error}</pre>
            )}

            {/* Parsed Job Preview */}
            {parsedJob && (
              <div style={styles.successBox}>
                <div style={styles.successHeader}>
                  <h3 style={styles.successTitle}>✓ Job Imported</h3>
                  <span style={styles.jobIdBadge}>ID: {jobId}</span>
                </div>
                
                <h4 style={styles.jobTitle}>{parsedJob.title}</h4>
                
                {parsedJob.focus_areas?.location && (
                  <p style={styles.jobMeta}>
                    <strong>Location:</strong> {parsedJob.focus_areas.location}
                  </p>
                )}
                
                {parsedJob.focus_areas?.employment_type && (
                  <p style={styles.jobMeta}>
                    <strong>Type:</strong> {parsedJob.focus_areas.employment_type}
                  </p>
                )}
                
                {parsedJob.description && (
                  <p style={styles.jobDescription}>{parsedJob.description}</p>
                )}
              </div>
            )}
          </div>

          {/* Right: Jobs List */}
          <div style={styles.card}>
            <div style={styles.jobsHeader}>
              <h2 style={styles.cardTitle}>
                Jobs for {selectedTenant?.name || 'Selected Client'}
              </h2>
              <button 
                style={styles.refreshButton}
                onClick={loadJobs} 
                disabled={loadingJobs || !selectedTenantId}
              >
                {loadingJobs ? '...' : '↻ Refresh'}
              </button>
            </div>

            {!selectedTenantId ? (
              <p style={styles.placeholderText}>Select a client to view jobs.</p>
            ) : loadingJobs ? (
              <p style={styles.loadingText}>Loading jobs...</p>
            ) : jobs.length === 0 ? (
              <p style={styles.placeholderText}>No jobs found for this client. Import one above!</p>
            ) : (
              <div style={styles.jobsList}>
                {jobs.map((job) => (
                  <div key={job.id} style={styles.jobCard}>
                    <div style={{ flex: 1 }}>
                      <div style={styles.jobCardTitle}>
                        {job.title}
                        {job.external_key && (
                          <a 
                            href={job.external_key} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={styles.externalLink}
                          >
                            ↗
                          </a>
                        )}
                      </div>
                      <div style={styles.jobCardMeta}>
                        {job.focus_areas?.location && <span>{job.focus_areas.location}</span>}
                        {job.focus_areas?.location && job.focus_areas?.employment_type && <span> • </span>}
                        {job.focus_areas?.employment_type && <span>{job.focus_areas.employment_type}</span>}
                      </div>
                      <div style={styles.jobCardId}>
                        ID: {job.id} • {new Date(job.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <button
                      style={styles.deleteButton}
                      onClick={() => handleDeleteJob(job.id)}
                      title="Delete job"
                    >
                      🗑
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// STYLES
// =============================================================================

const styles = {
  container: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #11181C 0%, #1a2632 50%, #0a0f14 100%)",
    color: "#fff",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px 40px",
    borderBottom: "1px solid rgba(255,255,255,0.1)"
  },
  logoContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 4
  },
  logoImage: {
    height: 48,
    width: "auto",
    objectFit: "contain"
  },
  subtitle: {
    fontSize: 14,
    color: "#9ca3af"
  },
  backButton: {
    padding: "8px 16px",
    background: "rgba(255,255,255,0.1)",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    fontSize: 14,
    cursor: "pointer"
  },
  content: {
    maxWidth: 1200,
    margin: "0 auto",
    padding: "40px 20px"
  },
  hero: {
    marginBottom: 24
  },
  heroTitle: {
    fontSize: "1.9rem",
    marginBottom: 6
  },
  heroText: {
    color: "#9ca3af",
    fontSize: "0.98rem",
    maxWidth: 640
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "minmax(0,1.2fr) minmax(0,1fr)",
    gap: 20,
    marginTop: 16
  },
  card: {
    background: "rgba(15, 23, 42, 0.9)",
    borderRadius: 18,
    padding: 18,
    border: "1px solid rgba(30, 64, 175, 0.6)",
    boxShadow: "0 18px 45px rgba(15, 23, 42, 0.75)"
  },
  cardTitle: {
    marginTop: 0,
    marginBottom: 16,
    fontSize: "1.2rem"
  },
  modeToggle: {
    display: "flex",
    gap: 8,
    marginBottom: 16
  },
  modeButton: {
    flex: 1,
    padding: "8px 12px",
    background: "rgba(255,255,255,0.1)",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
    fontSize: 14
  },
  modeButtonActive: {
    flex: 1,
    padding: "8px 12px",
    background: "#17c4c7",
    color: "#11181C",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 600
  },
  formGroup: {
    marginBottom: 12
  },
  label: {
    display: "block",
    marginBottom: 4,
    color: "#e5e7eb",
    fontSize: 14
  },
  input: {
    width: "100%",
    padding: "8px 12px",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 6,
    color: "#fff",
    fontSize: 14,
    marginTop: 4,
    boxSizing: "border-box"
  },
  textarea: {
    width: "100%",
    padding: "8px 12px",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 6,
    color: "#fff",
    fontSize: 14,
    marginTop: 4,
    resize: "vertical",
    fontFamily: "inherit",
    boxSizing: "border-box"
  },
  select: {
    padding: "8px 12px",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 6,
    color: "#fff",
    fontSize: 14,
    minWidth: 220
  },
  hint: {
    fontSize: "0.8rem",
    color: "#6b7280",
    marginTop: 4
  },
  loadingText: {
    color: "#cfd8ff"
  },
  errorText: {
    color: "#ff9f9f"
  },
  statusText: {
    marginTop: 8,
    color: "#cfd8ff"
  },
  submitButton: {
    padding: "8px 16px",
    background: "#17c4c7",
    color: "#11181C",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 14
  },
  errorBox: {
    background: "#400",
    padding: 10,
    marginTop: 16,
    whiteSpace: "pre-wrap",
    borderRadius: 8
  },
  successBox: {
    marginTop: 20,
    padding: 16,
    background: "rgba(23,196,199,0.1)",
    border: "1px solid rgba(23,196,199,0.4)",
    borderRadius: 8
  },
  successHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12
  },
  successTitle: {
    margin: 0,
    color: "#17c4c7"
  },
  jobIdBadge: {
    background: "rgba(23,196,199,0.2)",
    padding: "2px 8px",
    borderRadius: 4,
    fontSize: "0.85rem",
    fontFamily: "monospace"
  },
  jobTitle: {
    margin: "0 0 8px 0"
  },
  jobMeta: {
    margin: "4px 0",
    fontSize: "0.9rem",
    color: "#9ca3af"
  },
  jobDescription: {
    margin: "8px 0 0 0",
    fontSize: "0.9rem",
    color: "#9ca3af"
  },
  jobsHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12
  },
  refreshButton: {
    padding: "4px 10px",
    fontSize: "0.85rem",
    background: "rgba(255,255,255,0.1)",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    cursor: "pointer"
  },
  placeholderText: {
    color: "#9ca3af"
  },
  jobsList: {
    maxHeight: 400,
    overflowY: "auto"
  },
  jobCard: {
    padding: "10px 12px",
    marginBottom: 8,
    background: "rgba(15, 23, 42, 0.6)",
    border: "1px solid rgba(148, 163, 184, 0.2)",
    borderRadius: 8,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start"
  },
  jobCardTitle: {
    fontWeight: 600,
    marginBottom: 4
  },
  externalLink: {
    marginLeft: 8,
    color: "#60a5fa",
    fontSize: "0.85rem",
    textDecoration: "none"
  },
  jobCardMeta: {
    fontSize: "0.85rem",
    color: "#9ca3af"
  },
  jobCardId: {
    fontSize: "0.8rem",
    color: "#6b7280",
    marginTop: 4
  },
  deleteButton: {
    padding: "4px 8px",
    fontSize: "0.8rem",
    marginLeft: 8,
    background: "rgba(255,255,255,0.1)",
    color: "#fff",
    border: "none",
    borderRadius: 4,
    cursor: "pointer"
  }
};
